
import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { cn } from "@/lib/utils"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, alt, ...props }, ref) => {
  const [imageStatus, setImageStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading');

  const handleLoad = () => {
    setImageStatus('loaded');
  };

  const handleError = () => {
    setImageStatus('error');
  };

  // Reset state when src changes
  React.useEffect(() => {
    if (src) {
      // Check if it's from our storage (contains supabase and avatars bucket)
      const isOurStorageUrl = src.includes('supabase') && src.includes('avatars');
      if (isOurStorageUrl) {
        setImageStatus('loading'); // Start loading
      } else {
        setImageStatus('error'); // Don't load external URLs
      }
    } else {
      setImageStatus('error');
    }
  }, [src, alt]);

  // Only render the image if it's from our storage and hasn't errored
  if (!src || imageStatus === 'error') {
    return null; // This will cause AvatarFallback to be displayed
  }

  // Check if it's from our storage
  const isOurStorageUrl = src.includes('supabase') && src.includes('avatars');
  if (!isOurStorageUrl) {
    return null; // This will cause AvatarFallback to be displayed
  }

  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn("aspect-square h-full w-full", className)}
      src={src}
      alt={alt}
      onError={handleError}
      onLoad={handleLoad}
      {...props}
    />
  );
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, children, ...props }, ref) => {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-green-100 text-green-700 text-sm font-bold border-2 border-green-200",
        className
      )}
      {...props}
    >
      {children}
    </AvatarPrimitive.Fallback>
  );
})
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
